/**
 * InvestorDealView — /investor/deal/:token
 * Lightweight, unauthenticated read-only deal portal for external investors.
 * No platform login required. Token-based access only.
 * Shows: project summary, KPIs, financing signal, reports, express interest.
 */
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
import { RefreshCw, Download, TrendingDown, DollarSign, Shield,
         CheckCircle2, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function KPI({ label, value, unit, sub, color }) {
  return (
    <MetricCard label={label} value={value ?? "—"} unit={unit} sub={sub} color={color || "text-slate-900"} />
  );
}

export default function InvestorDealView() {
  const { token } = useParams();
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [interest, setInterest] = useState(null);
  const [message,  setMessage]  = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [dlLoading,  setDlLoading]  = useState(null);
  const [showMore,   setShowMore]   = useState(false);

  useEffect(() => {
    fetch(`${API}/api/investor/${token}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e === 404 ? "Deal not found" : "Link expired or invalid"); setLoading(false); });
  }, [token]);

  const handleExpressInterest = async () => {
    if (!interest) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/investor/${token}/express-interest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ interest_level: interest, message }),
      });
      if (res.ok) {
        setSubmitted(true);
      }
    } catch {}
    setSubmitting(false);
  };

  const handleDownload = async (key, label, endpoint) => {
    setDlLoading(key);
    try {
      const res = await fetch(`${API}${endpoint}`);
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${data?.project?.project_name || "Deal"}-${label}.pdf`;
      a.click(); URL.revokeObjectURL(a.href);
    } catch {}
    setDlLoading(null);
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-[#0F172A]">
      <RefreshCw size={24} className="animate-spin text-teal-400" />
    </div>
  );

  if (error) return (
    <div className="flex items-center justify-center min-h-screen bg-[#F8FAFC]">
      <div className="text-center max-w-sm">
        <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-4">
          <AlertTriangle size={24} className="text-red-500" />
        </div>
        <h2 className="text-xl font-bold text-slate-900 mb-2">Access Unavailable</h2>
        <p className="text-sm text-slate-500">{error}</p>
      </div>
    </div>
  );

  const p    = data?.project || {};
  const recs = data?.available_reports || [];
  const SIGNAL_STYLES = {
    STRONG:     "bg-emerald-600",
    MODERATE:   "bg-amber-500",
    DEVELOPING: "bg-slate-500",
  };
  const DSCR_COLOR = (p.dscr_flag === "strong" || p.dscr_flag === "acceptable") ? "text-emerald-600" :
                     p.dscr_flag === "weak" ? "text-amber-600" : "text-red-600";

  const INTEREST_OPTS = [
    { value: "exploring",     label: "Exploring",          desc: "Reviewing materials, no commitment yet" },
    { value: "interested",    label: "Interested",          desc: "Would like to learn more" },
    { value: "proceeding_dd", label: "Proceeding to DD",   desc: "Ready to start due diligence" },
    { value: "committed",     label: "Committed",          desc: "Prepared to issue term sheet" },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <div className="bg-[#0F172A] px-6 py-5">
        <div className="flex items-start justify-between">
          <div>
            
      <PageHeader
        label="DeCarbonIQ — Climate Finance"
        title={p.project_name}
      />
            <p className="text-xs text-slate-400 mt-0.5">
              {p.sector_label} · {p.country} · {p.methodology}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold px-3 py-1.5 rounded-full text-white ${SIGNAL_STYLES[p.financing_signal] || SIGNAL_STYLES.DEVELOPING}`}>
              {p.financing_signal || "—"} SIGNAL
            </span>
          </div>
        </div>
      </div>

      {/* VVB disclaimer */}
      <div className="bg-amber-50 border-b border-amber-200 px-6 py-2">
        <p className="text-xs text-amber-700 text-center max-w-4xl mx-auto">
          <strong>{p.platform_only_note}</strong> · All documents are platform-generated. External VVB validation required for carbon credit issuance.
        </p>
      </div>

      <div className="px-6 py-6 space-y-5">

        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <KPI label="Annual Reduction" value={p.annual_tco2e ? `~${Number(p.annual_tco2e).toLocaleString()}` : null} unit="tCO₂e/yr" color="text-teal-700" />
          <KPI label="Total CAPEX"      value={p.capex_usd ? `$${Number(p.capex_usd).toLocaleString()}` : null}                     color="text-slate-900" />
          <KPI label="DSCR (blended)"   value={p.dscr_base ? `${p.dscr_base}x` : null} sub={p.dscr_flag?.replace(/_/g," ")} color={DSCR_COLOR} />
          <KPI label="Data Confidence"  value={p.data_confidence_label} color={p.data_confidence_label === "High" ? "text-emerald-600" : "text-amber-600"} />
        </div>

        {/* Deal info */}
        <SectionCard>

          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Deal Overview</p>
          <div className="grid grid-cols-2 gap-4 text-xs">
            {[
              ["Organisation",        p.org_name],
              ["Sector",              p.sector_label],
              ["Pathway",             (p.recommended_pathway||"—").replace(/_/g," ").toUpperCase()],
              ["Annual savings",      p.annual_savings_usd ? `$${Number(p.annual_savings_usd).toLocaleString()}/yr` : "—"],
              ["Data confidence",     p.data_confidence_label],
              ["Platform status",     p.platform_only_note],
            ].map(([l, v]) => (
              <div key={l} className="flex flex-col">
                <span className="text-xs text-slate-400">{l}</span>
                <span className="font-semibold text-slate-700">{v || "—"}</span>
              </div>
            ))}
          </div>
        
        </SectionCard>

        {/* Invitee message */}
        {data?.invitee?.message && (
          <div className="bg-teal-50 border border-teal-200 rounded-xl px-4 py-3">
            <p className="text-xs text-teal-600 font-semibold mb-1">Note from deal team:</p>
            <p className="text-xs text-teal-800 italic">"{data.invitee.message}"</p>
          </div>
        )}

        {/* Reports */}
        <SectionCard>

          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Deal Documents</p>
          <div className="space-y-2">
            {recs.map(r => (
              <div key={r.key} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                <p className="text-xs font-semibold text-slate-700">{r.label}</p>
                <button onClick={() => handleDownload(r.key, r.label, r.endpoint)}
                  disabled={dlLoading === r.key}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0F172A] text-white text-xs font-bold rounded-lg hover:bg-slate-800 disabled:opacity-50">
                  {dlLoading === r.key ? <RefreshCw size={10} className="animate-spin" /> : <Download size={10} />}
                  Download
                </button>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 italic mt-3">{data?.platform_disclaimer?.slice(0,120)}...</p>
        
        </SectionCard>

        {/* Express Interest */}
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="express-interest">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Express Interest</p>
          {submitted ? (
            <div className="flex items-center gap-2 text-emerald-600">
              <CheckCircle2 size={16} />
              <p className="text-sm font-bold">Interest recorded. The deal team will be in touch.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {INTEREST_OPTS.map(opt => (
                  <button key={opt.value}
                    onClick={() => setInterest(opt.value)}
                    className={`text-left px-3 py-2.5 rounded-xl border-2 transition-all ${
                      interest === opt.value
                        ? "border-teal-500 bg-teal-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}>
                    <p className="text-xs font-bold text-slate-900">{opt.label}</p>
                    <p className="text-xs text-slate-400">{opt.desc}</p>
                  </button>
                ))}
              </div>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Optional: add a note to the deal team..."
                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl mb-3 resize-none"
                rows={2}
              />
              <button onClick={handleExpressInterest}
                disabled={!interest || submitting}
                className="w-full py-2.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 disabled:opacity-50">
                {submitting ? "Submitting..." : "Submit Interest →"}
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-xs text-slate-400 italic text-center pb-4">
          This deal package was prepared by DeCarbonIQ. All information is indicative and subject to due diligence.
          DeCarbonIQ is not a licensed financial adviser. Documents are platform-generated, not third-party validated.
        </p>
      </div>
    </div>
  );
}
